/**
 * Provider-neutral hosted payment sessions.
 *
 * Card data is entered on the provider's hosted page and never passes through
 * this application. The browser notification only triggers a server-side
 * status check; it is not treated as proof of payment.
 */
import JSDB from 'fromKotlin/nk';

const API_BASE = 'https://nihulkav.online';

function getHeaders(): HeadersInit {
    const token = (JSDB() as any).getToken?.();
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
}

function getCompanyId(): string {
    try {
        const db = JSDB() as any;
        if (typeof db.getCompanyId === 'function') return String(db.getCompanyId());
        return '';
    } catch {
        return '';
    }
}

async function post<T>(path: string, body: Record<string, any>): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({company_id: getCompanyId(), ...body}),
    });
    const json = await res.json();
    if (json?.status !== 'SUCCESS') {
        throw new Error(json?.status_msg || json?.msg || 'שגיאה');
    }
    const message = json.status_msg;
    if (typeof message === 'string') {
        try {
            return JSON.parse(message) as T;
        } catch {
            return message as unknown as T;
        }
    }
    return message as T;
}

export interface PaymentSession {
    session_id: string;
    checkout_url: string;
    amount: number;
    provider: string;
    /** Temporary aliases returned by older hosted-payment servers. */
    iframe_url?: string;
    sum?: number;
}

export interface PaymentSessionStatus {
    /**
     * ``failed`` is a final decline and the only status safe to retry.
     * ``needs_review`` means the charge may have gone through without the
     * server being able to confirm it — never offer another attempt.
     */
    status: 'pending' | 'paid' | 'failed' | 'needs_review';
    transaction_id: string | null;
    amount: number;
    last4: string | null;
    provider: string;
    /** Temporary alias returned by older hosted-payment servers. */
    sum?: number;
    /** Reusable provider token (not a PAN), when supported by the provider. */
    token?: string | null;
    /** MM/YYYY expiry of the reusable token. */
    token_expiry?: string | null;
    receipt_state?: 'pending' | 'creating' | 'issued' | 'failed' | null;
    receipt_id?: number | null;
    receipt_error?: string | null;
}

export interface PaymentSessionOptions {
    purpose?: string;
    ref?: string;
    client_id?: number;
    /** Open customer invoices this payment should settle; omit for FIFO allocation. */
    invoice_ids?: number[];
    contact?: string;
    company?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    zip?: string;
    /** Hosted checkout method. ``bit`` uses Tranzila Bit Init (QR), not the card iframe. */
    method?: 'bit' | 'credit';
    payment_plan?: number;
    /**
     * Stable key for one payment intent, reused across retries so the
     * provider can refuse to charge the same payment twice.
     */
    intent_key?: string;
}

export function createPaymentSession(
    amount: number,
    opts?: PaymentSessionOptions,
): Promise<PaymentSession> {
    return post<PaymentSession>('/pay/session/create', {amount, ...(opts ?? {})});
}

export function getPaymentSessionStatus(sessionId: string): Promise<PaymentSessionStatus> {
    return post<PaymentSessionStatus>('/pay/session/status', {session_id: sessionId});
}
